// DS2 Switch: on / off, with an optional label (the children) at the right. The native checkbox with role="switch"
// (Space, focus and screen readers as usual): a click anywhere on the label toggles it natively and calls
// onChange(checked). colors: the track's colour on, off, on and disabled, off and disabled (any CSS colour; each one
// left out keeps the DS2 one). className goes to the root, the other props to the input.
import cx from "classnames";
import type { CSSProperties, InputHTMLAttributes, Ref } from "react";
import { CheckIcon, CrossIcon } from "./icons";
import styles from "./Switch.module.css";

export type SwitchProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "checked" | "onChange" | "disabled" | "id" | "role"
> & {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  colors?: { on?: string; off?: string; onDisabled?: string; offDisabled?: string };
  ref?: Ref<HTMLInputElement>;
};

export function Switch({ id, checked, onChange, disabled, colors, children, className, ref, ...props }: SwitchProps) {
  return (
    <label
      className={cx(styles.root, disabled && styles.disabled, className)}
      style={
        {
          "--switch-on": colors?.on,
          "--switch-off": colors?.off,
          "--switch-on-disabled": colors?.onDisabled,
          "--switch-off-disabled": colors?.offDisabled,
        } as CSSProperties
      }
      onClick={(e) => e.currentTarget.control?.focus()}
    >
      <span className={styles.track}>
        <input
          ref={ref}
          id={id}
          type="checkbox"
          role="switch"
          className={styles.switch}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.currentTarget.checked)}
          {...props}
        />
        <CrossIcon className={styles.off} width="8" height="8" />
        <CheckIcon className={styles.on} width="11" height="11" />
      </span>
      {children}
    </label>
  );
}
